import React, { useCallback, useEffect, useState } from "react";
import { Alert, Box, Button, Card, CardContent, CircularProgress, TextField, Typography } from "@mui/material";
import { errorText, loadConfig, p4u } from "./api";
import { getAssertion } from "./webauthn";
import { Countdown, Evidence } from "./components";

/**
 * Approver (phone) page: a web stand-in for the mobile SDK with the same API contract.
 * The push carries the requestId; the app loads the details, then approves or rejects
 * with the user's passkey (biometric / PIN):
 *   GET  /approvals/:id?clientId=
 *   POST /approvals/:id/options  { clientId, intent }
 *   POST /approvals/:id/verify   { clientId, credential, platform }
 *   POST /approvals/:id/reject   { clientId, credential, platform, reason, fraud }
 */

function RequestCard({ config, request, onDone }) {
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);

  const decide = async (intent, fraud = false) => {
    setBusy(true);
    setMessage(null);
    try {
      const body = { clientId: config.clientId };
      const options = await p4u().post(`/approvals/${request.requestId}/options`, { ...body, intent });
      if (options.status !== 200) throw new Error(errorText(options.data));
      const credential = await getAssertion(options.data);
      const path = intent === "reject" ? "reject" : "verify";
      const res = await p4u().post(`/approvals/${request.requestId}/${path}`, {
        ...body,
        credential,
        platform: "web",
        ...(intent === "reject" ? { reason: fraud ? "fraud" : "USER_REJECTED", fraud } : {}),
      });
      if (res.status !== 200) throw new Error(errorText(res.data));
      setMessage({ severity: intent === "reject" ? "info" : "success", text: intent === "reject" ? "Rejected" : "Approved" });
      onDone();
    } catch (e) {
      setMessage({ severity: "error", text: e.message || "Failed" });
    } finally {
      setBusy(false);
    }
  };

  const d = request.details || {};
  const where = [request.initiator?.city, request.initiator?.country].filter(Boolean).join(", ");
  const open = ["PENDING", "CHALLENGE_ISSUED"].includes(request.status);

  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardContent>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="h6">Approve payment</Typography>
          {open && <Countdown expiresAt={request.expiresAt} />}
        </Box>
        <Typography variant="body2" color="text.secondary">
          {request.appName} · requested from {request.initiator?.device || "unknown device"} {request.initiator?.browser || ""}
          {where && ` · ${where}`}
        </Typography>
        <Evidence
          rows={[
            ["Amount", `${d.currency} ${d.amount}`],
            ["To", d.beneficiaryName],
            ["Account", d.beneficiaryAccount],
            ["Bank code", d.beneficiaryBankCode],
            ["Reference", d.reference],
            ["Status", request.status],
          ]}
        />
        {open && (
          <>
            <Box sx={{ display: "flex", gap: 1, mt: 2, flexWrap: "wrap" }}>
              <Button variant="contained" color="success" onClick={() => decide("approve")} disabled={busy}>
                Approve
              </Button>
              <Button variant="outlined" color="error" onClick={() => decide("reject")} disabled={busy}>
                Reject
              </Button>
            </Box>
            <Button size="small" color="error" sx={{ mt: 1 }} onClick={() => decide("reject", true)} disabled={busy}>
              Not me: reject and report fraud
            </Button>
          </>
        )}
        {busy && <CircularProgress size={18} sx={{ ml: 2 }} />}
        {message && (
          <Alert severity={message.severity} sx={{ mt: 1 }}>
            {message.text}
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}

export default function Approver() {
  const [config, setConfig] = useState(null);
  const [requestId, setRequestId] = useState(() => new URLSearchParams(window.location.search).get("requestId") || "");
  const [request, setRequest] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    loadConfig().then(setConfig).catch((e) => setError(e.message));
  }, []);

  const load = useCallback(async () => {
    if (!config || !requestId) return;
    setError("");
    const res = await p4u().get(`/approvals/${encodeURIComponent(requestId.trim())}`, { params: { clientId: config.clientId } });
    if (res.status === 200) setRequest(res.data);
    else {
      setRequest(null);
      setError(errorText(res.data));
    }
  }, [config, requestId]);

  useEffect(() => {
    if (config && requestId) load();
    // load once the config is ready; later loads come from the button
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config]);

  if (!config) return error ? <Alert severity="error">{error}</Alert> : <CircularProgress />;

  return (
    <Box sx={{ maxWidth: 560 }}>
      <h1 className="pwl_heading">Approver (phone)</h1>
      <p className="pwl_subheading">
        Stand-in for the Passwordless mobile app. In the app, the request id arrives in the push notification.
      </p>
      <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
        <TextField size="small" fullWidth label="Request id" value={requestId} onChange={(e) => setRequestId(e.target.value)} />
        <Button variant="contained" onClick={load} disabled={!requestId}>
          Load
        </Button>
      </Box>
      {error && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}
      {request && <RequestCard config={config} request={request} onDone={load} />}
    </Box>
  );
}
